// Text on the GPU, set by the browser. Each label is typeset once with the browser's own text engine (so
// every font, accent and Vietnamese letter is right) into a shared texture, then drawn as a quad that the
// GPU places, scales and fades each frame. Sizes are rendered in steps; between steps a label is scaled a
// little, and a crisper copy is made when there is time.

import {
  DataTexture,
  DoubleSide,
  GLSL3,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  LinearFilter,
  Mesh,
  PlaneGeometry,
  RawShaderMaterial,
  RedFormat,
  UnsignedByteType,
} from "three";
import type { WebGLRenderer } from "three";
import { EDITION } from "./glsl.ts";
import type { SharedUniforms } from "./stage.ts";

export const ATLAS_SIZE = 4096;
/** Rendered sizes (css px). A label is drawn from the smallest step at least as big as it appears. */
const STEPS = [9, 11, 13, 15.5, 18.5, 22, 26, 31, 37, 44, 52, 62];
const PAD = 2;

export interface TextStyle {
  font: string;
  weight?: number;
  italic?: boolean;
  /** Extra space between letters, in em. */
  tracking?: number;
  upper?: boolean;
}

interface Slot {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Size of one em in device px, as rendered. */
  em: number;
  /** Where the text's box starts inside the slot, in device px. */
  baseline: number;
  used: number;
}

/** What to draw this frame: text, where its centre goes, how big, which way up. */
export interface LabelDraw {
  text: string;
  style: TextStyle;
  /** Centre, css px. */
  x: number;
  y: number;
  /** Em size on screen, css px. */
  size: number;
  alpha: number;
  /** 0 = ink, 1 = soft ink. */
  tone?: number;
  /** Radians; kept at 0 in the atlas, where every word lies flat. */
  angle?: number;
  /** 0 is drawn with the chart; 1 over everything else (the needle's tags). */
  layer?: 0 | 1;
}

interface Batch {
  mesh: Mesh;
  geometry: InstancedBufferGeometry;
  capacity: number;
  queue: LabelDraw[];
}

export class Labels {
  /** One mesh per layer, sharing the sheet of typeset text. */
  readonly meshes: Mesh[];
  private readonly batches: Batch[];
  private readonly texture: DataTexture;
  private readonly slots = new Map<string, Slot>();
  private shelf = { x: 0, y: 0, h: 0 };
  private readonly scratch: CanvasRenderingContext2D;
  private readonly dpr: number;
  private frame = 0;
  private readonly renderer: WebGLRenderer;
  /** Labels typeset this frame; the rest wait, using a near size meanwhile. */
  budget = 24;
  /** For measuring: labels typeset, and times the sheet was wiped. */
  readonly stats = { typeset: 0, wipes: 0, slots: 0 };

  constructor(shared: SharedUniforms, renderer: WebGLRenderer) {
    this.renderer = renderer;
    this.dpr = renderer.getPixelRatio();
    this.texture = new DataTexture(
      new Uint8Array(ATLAS_SIZE * ATLAS_SIZE),
      ATLAS_SIZE,
      ATLAS_SIZE,
      RedFormat,
      UnsignedByteType,
    );
    this.texture.magFilter = this.texture.minFilter = LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.unpackAlignment = 1;
    this.texture.needsUpdate = true;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("This browser cannot draw text on a canvas.");
    this.scratch = ctx;
    const material = new RawShaderMaterial({
      glslVersion: GLSL3,
      uniforms: { ...shared, uAtlas: { value: this.texture } },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      // Screen space runs y down, which turns the quads over.
      side: DoubleSide,
      vertexShader: /* glsl */ `
        precision highp float;
        uniform vec2 uViewport;
        in vec3 position;
        in vec4 aBox;    // centre x, y; width, height (css px)
        in vec4 aUv;     // u0, v0, u1, v1
        in vec3 aStyle;  // alpha, tone, angle
        out vec2 vUv;
        out vec2 vStyle;
        void main() {
          vec2 local = (position.xy - 0.5) * aBox.zw;
          float c = cos(aStyle.z), s = sin(aStyle.z);
          vec2 p = aBox.xy + vec2(local.x * c - local.y * s, local.x * s + local.y * c);
          vUv = mix(aUv.xy, aUv.zw, position.xy);
          vStyle = aStyle.xy;
          gl_Position = vec4(p.x / uViewport.x * 2.0 - 1.0, 1.0 - p.y / uViewport.y * 2.0, 0.0, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${EDITION}
        uniform sampler2D uAtlas;
        in vec2 vUv;
        in vec2 vStyle;
        out vec4 fragColor;
        void main() {
          float a = texture(uAtlas, vUv).r * vStyle.x;
          if (a < 0.003) discard;
          vec3 ink = inkTone();
          fragColor = vec4(ink, a * mix(1.0, 0.74, vStyle.y));
        }`,
    });
    this.batches = [100, 200].map((order) => {
      const geometry = makeGeometry(256);
      const mesh = new Mesh(geometry, material);
      mesh.frustumCulled = false;
      mesh.renderOrder = order;
      return { mesh, geometry, capacity: 256, queue: [] };
    });
    this.meshes = this.batches.map((b) => b.mesh);
  }

  /** The width of a text at 1 px em, for fitting. Cached. */
  private widths = new Map<string, number>();
  measure(text: string, style: TextStyle): number {
    const key = `${fontOf(style, 100)}|${style.tracking ?? 0}|${text}`;
    let w = this.widths.get(key);
    if (w == null) {
      const ctx = this.scratch;
      ctx.font = fontOf(style, 100);
      const t = style.upper ? text.toUpperCase() : text;
      w = (ctx.measureText(t).width + (style.tracking ?? 0) * 100 * Math.max(0, [...t].length - 1)) / 100;
      this.widths.set(key, w);
    }
    return w;
  }

  /** Queues a label for this frame. */
  draw(label: LabelDraw): void {
    if (label.alpha > 0.004 && label.size >= 4) (this.batches[label.layer ?? 0] as Batch).queue.push(label);
  }

  /** Lays out everything queued this frame. Returns whether some labels are still waiting to be typeset. */
  flush(): boolean {
    this.frame++;
    this.typesetThisFrame = 0;
    let waiting = false;
    for (const batch of this.batches) waiting = this.flushBatch(batch) || waiting;
    return waiting;
  }

  private typesetThisFrame = 0;

  private flushBatch(batch: Batch): boolean {
    let waiting = false;
    const n = batch.queue.length;
    if (n > batch.capacity) {
      batch.geometry.dispose();
      batch.capacity = Math.ceil(n * 1.5);
      batch.geometry = makeGeometry(batch.capacity);
      batch.mesh.geometry = batch.geometry;
    }
    const box = batch.geometry.getAttribute("aBox") as InstancedBufferAttribute;
    const uv = batch.geometry.getAttribute("aUv") as InstancedBufferAttribute;
    const st = batch.geometry.getAttribute("aStyle") as InstancedBufferAttribute;
    const B = box.array as Float32Array,
      U = uv.array as Float32Array,
      S = st.array as Float32Array;
    let k = 0;
    for (const l of batch.queue) {
      const step = STEPS.find((s) => s >= l.size * 0.94) ?? (STEPS[STEPS.length - 1] as number);
      let slot = this.slots.get(keyOf(l.text, l.style, step));
      if (!slot) {
        if (this.typesetThisFrame < this.budget) {
          slot = this.typeset(l.text, l.style, step);
          this.typesetThisFrame++;
        } else {
          waiting = true;
          // Meanwhile use the nearest size already made, if any.
          for (const s of STEPS) {
            const near = this.slots.get(keyOf(l.text, l.style, s));
            if (near && (!slot || Math.abs(s - step) < Math.abs(slot.em / this.dpr - step))) slot = near;
          }
        }
      }
      if (!slot) continue;
      slot.used = this.frame;
      const scale = l.size / (slot.em / this.dpr);
      B.set([l.x, l.y, (slot.w / this.dpr) * scale, (slot.h / this.dpr) * scale], k * 4);
      U.set(
        [slot.x / ATLAS_SIZE, slot.y / ATLAS_SIZE, (slot.x + slot.w) / ATLAS_SIZE, (slot.y + slot.h) / ATLAS_SIZE],
        k * 4,
      );
      S.set([l.alpha, l.tone ?? 0, l.angle ?? 0], k * 3);
      k++;
    }
    batch.geometry.instanceCount = k;
    for (const [attr, size] of [
      [box, 4],
      [uv, 4],
      [st, 3],
    ] as const) {
      attr.needsUpdate = true;
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, k * size);
    }
    batch.queue = [];
    return waiting;
  }

  private typeset(text: string, style: TextStyle, step: number): Slot | undefined {
    const ctx = this.scratch;
    const em = Math.round(step * this.dpr);
    const t = style.upper ? text.toUpperCase() : text;
    const font = fontOf(style, em);
    ctx.font = font;
    const m = ctx.measureText(t);
    const tracking = (style.tracking ?? 0) * em;
    const w = Math.ceil(m.width + tracking * Math.max(0, [...t].length - 1)) + PAD * 2;
    const ascent = Math.ceil(em * 0.95),
      descent = Math.ceil(em * 0.32);
    const h = ascent + descent + PAD * 2;
    if (w > ATLAS_SIZE) return undefined;
    const place = this.allocate(w, h);
    if (!place) return undefined;
    const canvas = ctx.canvas;
    if (canvas.width < w || canvas.height < h) {
      canvas.width = Math.max(canvas.width, w);
      canvas.height = Math.max(canvas.height, h);
    }
    ctx.clearRect(0, 0, w, h);
    ctx.font = font;
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "alphabetic";
    ctx.letterSpacing = `${tracking}px`;
    ctx.fillText(t, PAD, PAD + ascent);
    ctx.letterSpacing = "0px";
    const pixels = ctx.getImageData(0, 0, w, h).data;
    const alpha = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) alpha[i] = pixels[i * 4 + 3] as number;
    this.upload(place.x, place.y, w, h, alpha);
    const slot: Slot = { x: place.x, y: place.y, w, h, em, baseline: ascent, used: this.frame };
    this.slots.set(keyOf(text, style, step), slot);
    this.stats.typeset++;
    this.stats.slots = this.slots.size;
    return slot;
  }

  /** Shelf packing; when the sheet is full it is wiped and labels are typeset again as they are needed. */
  private allocate(w: number, h: number): { x: number; y: number } | null {
    const s = this.shelf;
    if (s.x + w > ATLAS_SIZE) {
      s.y += s.h + 1;
      s.x = 0;
      s.h = 0;
    }
    if (s.y + h > ATLAS_SIZE) {
      this.stats.wipes++;
      this.slots.clear();
      this.shelf = { x: 0, y: 0, h: 0 };
      return this.allocate(w, h);
    }
    const at = { x: s.x, y: s.y };
    s.x += w + 1;
    s.h = Math.max(s.h, h);
    return at;
  }

  private upload(x: number, y: number, w: number, h: number, data: Uint8Array): void {
    const renderer = this.renderer;
    const gl = renderer.getContext() as WebGL2RenderingContext;
    // Make sure three has created the texture, then write just this rectangle into it.
    renderer.initTexture(this.texture);
    const handle = (renderer.properties.get(this.texture) as { __webglTexture?: WebGLTexture }).__webglTexture;
    if (!handle) return;
    renderer.state.activeTexture(gl.TEXTURE0);
    renderer.state.bindTexture(gl.TEXTURE_2D, handle);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, w, h, gl.RED, gl.UNSIGNED_BYTE, data);
  }
}

function makeGeometry(capacity: number): InstancedBufferGeometry {
  const quad = new PlaneGeometry(1, 1).translate(0.5, 0.5, 0);
  const g = new InstancedBufferGeometry();
  g.index = quad.index;
  g.setAttribute("position", quad.getAttribute("position"));
  g.setAttribute("aBox", new InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(35048));
  g.setAttribute("aUv", new InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(35048));
  g.setAttribute("aStyle", new InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(35048));
  g.instanceCount = 0;
  return g;
}

const fontOf = (s: TextStyle, px: number) => `${s.italic ? "italic " : ""}${s.weight ?? 400} ${px}px ${s.font}`;
const keyOf = (text: string, s: TextStyle, step: number) =>
  `${step}|${fontOf(s, 1)}|${s.tracking ?? 0}|${s.upper ? 1 : 0}|${text}`;
